/**
 * Search by photo: the pure half (validation, request body, response parsing).
 * The network call lives in `services/image-search-service.ts` so this file
 * stays free of the API client and unit-testable.
 */

/** Path under VITE_API_BASE_URL (the shared API client already prefixes it). */
export const IMAGE_SEARCH_ENDPOINT = "/search/image";

/** Multipart field name the backend reads the photo from. */
export const IMAGE_SEARCH_FIELD = "image";

/** Client-side cap on the uploaded photo. */
export const IMAGE_SEARCH_MAX_BYTES = 8 * 1024 * 1024;

/** How many returned offers are rendered. */
export const IMAGE_SEARCH_MAX_RESULTS = 100;

export type ImageSearchFileProblem = "not_image" | "too_large";

/** null when the file may be uploaded. */
export function validateImageFile(file: {
  type?: string;
  size?: number;
}): ImageSearchFileProblem | null {
  if (!file.type || !file.type.startsWith("image/")) return "not_image";
  if (!file.size) return "not_image"; // empty file
  if (file.size > IMAGE_SEARCH_MAX_BYTES) return "too_large";
  return null;
}

/** The multipart body: one `image` part. */
export function buildImageSearchFormData(file: Blob, fileName?: string): FormData {
  const form = new FormData();
  const name = fileName || (file as File).name || "photo";
  form.append(IMAGE_SEARCH_FIELD, file, name);
  return form;
}

export interface ImageSearchResult {
  offerIds: string[];
  productIds: string[];
}

/**
 * Read `{ statusCode, success, data: { offerIds, productIds } }`. Returns null
 * for anything that is not a successful answer (`success: false`, an error
 * body, a malformed payload).
 */
export function parseImageSearchResponse(payload: unknown): ImageSearchResult | null {
  const body = payload as
    | { success?: unknown; data?: { offerIds?: unknown; productIds?: unknown } }
    | null
    | undefined;
  if (!body || body.success !== true || !body.data) return null;
  const strings = (value: unknown): string[] =>
    Array.isArray(value)
      ? value.filter((v): v is string => typeof v === "string" && v !== "")
      : [];
  return {
    offerIds: strings(body.data.offerIds),
    productIds: strings(body.data.productIds),
  };
}
